import { useState } from 'react';

import {
  Autocomplete,
  Box,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  DateField,
  type DateFieldProps,
  DateTimeField,
} from '@mui/x-date-pickers-pro';

import {
  addWhereClause,
  deleteWhereClause,
} from '@modules/_shared/components/filters/Filters';
import { useSchema } from '@modules/_shared/components/filters/useSchema';
import { isSQLIntervalString } from '@modules/_shared/utils/sqlInterval';

import {
  DeleteButton,
  autocompleteClearIcon,
  autocompletePopupIcon,
  autocompleteSx,
} from './DeleteButton';

interface ScalarProps {
  name: string;
  node: any;
  path: string[];
  where: any;
  setWhere: (where: any) => void;
}

const compactFieldSx = {
  '& .MuiInputBase-input': {
    py: '3px !important',
    px: '8px !important',
  },
} as const;

const inputSx = {
  bgcolor: 'background.paper',
  width: 200,
  ...compactFieldSx,
} as const;

const compactInputSx = {
  bgcolor: 'background.paper',
  width: 44,
  ...compactFieldSx,
  '& .MuiInputBase-input': {
    py: '3px !important',
    px: '4px !important',
  },
} as const;

const selectSx = {
  bgcolor: 'background.paper',
  width: 150,
  '& .MuiSelect-select': {
    py: '3px !important',
    px: '8px !important',
  },
} as const;

const SelectIcon = ({ className }: { className?: string }) => (
  <Box
    component="span"
    className={className}
    sx={{ display: 'flex', alignItems: 'center', color: 'action.active' }}
  >
    {autocompletePopupIcon}
  </Box>
);

const parseTimeFilterValue = (intervalOrDateString: string) => {
  const interval: {
    years?: number;
    months?: number;
    days?: number;
    hours?: number;
    minutes?: number;
  } = {
    years: undefined,
    months: undefined,
    days: undefined,
    hours: undefined,
    minutes: undefined,
  };

  if (!intervalOrDateString) {
    return { dateType: 'exact_date', date: new Date(), interval };
  }

  if (isSQLIntervalString(intervalOrDateString)) {
    const dateType = intervalOrDateString.includes('now() -')
      ? 'days_ago'
      : 'days_from_now';
    intervalOrDateString = intervalOrDateString
      .replace('now() -', '')
      .replace('now() +', '');

    const regex = /(\d{1,4})\s{1,5}(years?|months?|days?|hours?|minutes?)/g;
    let match;
    while ((match = regex.exec(intervalOrDateString)) != null) {
      const value = parseInt(match[1]);
      const unit = match[2];
      if (unit.includes('year')) {
        interval.years = value;
      } else if (unit.includes('month')) {
        interval.months = value;
      } else if (unit.includes('day')) {
        interval.days = value;
      } else if (unit.includes('hour')) {
        interval.hours = value;
      } else if (unit.includes('minute')) {
        interval.minutes = value;
      }
    }

    return { dateType, date: null, interval };
  } else {
    return {
      dateType: 'exact_date',
      date: new Date(intervalOrDateString),
      interval,
    };
  }
};

const generateIntervalString = (
  interval: {
    years?: number;
    months?: number;
    days?: number;
    hours?: number;
    minutes?: number;
  },
  dateType: string,
) => {
  const intervalString = Object.entries(interval)
    .filter(([_, val]) => val)
    .map(([key, val]) => `${val} ${val === 1 ? key.slice(0, -1) : key}`)
    .join(' ');

  return dateType === 'days_ago'
    ? `now() - ${intervalString}`
    : `now() + ${intervalString}`;
};

const TimestamptzField = ({
  defaultValue,
  where,
  path,
  onChange,
  dateOnly = false,
}: {
  defaultValue: any;
  where: any;
  path: string[];
  onChange: (value: any) => void;
  dateOnly?: boolean;
}) => {
  const getValueFromPath = (obj: any, path: string[]) => {
    return path.reduce(
      (acc, key) => (acc && acc[key] !== 'undefined' ? acc[key] : null),
      obj,
    );
  };

  const valueAtPath = getValueFromPath(where, path);
  const intervalOrDateString = valueAtPath || defaultValue;
  const {
    dateType: initialDateType,
    date,
    interval: initialInterval,
  } = parseTimeFilterValue(intervalOrDateString);

  const [interval, setInterval] = useState(initialInterval);
  const [dateType, setDateType] = useState(initialDateType);

  const handleIntervalChange = (field: keyof typeof interval, value: any) => {
    const newInterval = { ...interval, [field]: parseInt(value) };
    setInterval(newInterval);
    const intervalString = generateIntervalString(newInterval, dateType);
    onChange(intervalString);
  };

  const handleDateTypeChange = (value: string) => {
    setDateType(value);
    let newValue;
    if (value === 'exact_date') {
      newValue = new Date().toISOString();
    } else if (value === 'days_from_now') {
      newValue = generateIntervalString(interval, 'days_from_now');
    } else if (value === 'days_ago') {
      newValue = generateIntervalString(interval, 'days_ago');
    }
    onChange(newValue);
  };

  const dateProps: Pick<
    DateFieldProps,
    'value' | 'variant' | 'size' | 'sx' | 'clearable'
  > = {
    value: date,
    variant: 'outlined',
    size: 'small',
    clearable: true,
    sx: inputSx,
  };

  return (
    <Stack
      direction="row"
      sx={{
        gap: 0.5,
        alignItems: 'center',
      }}
    >
      <Select
        value={dateType}
        onChange={e => handleDateTypeChange(e.target.value)}
        variant="outlined"
        size="small"
        IconComponent={SelectIcon}
        sx={selectSx}
      >
        <MenuItem value="exact_date">Exact date</MenuItem>
        <MenuItem value="days_from_now">Days from now</MenuItem>
        <MenuItem value="days_ago">Days ago</MenuItem>
      </Select>
      {dateType === 'exact_date' &&
        (dateOnly ? (
          <DateField
            onChange={(date, { validationError }) => {
              if (!validationError && date) {
                onChange(date);
              }
            }}
            {...dateProps}
          />
        ) : (
          <DateTimeField
            onChange={(date, { validationError }) => {
              if (!validationError && date) {
                onChange(date);
              }
            }}
            {...dateProps}
          />
        ))}
      {['days_ago', 'days_from_now'].includes(dateType) && (
        <>
          {(['years', 'months', 'days', 'hours', 'minutes'] as const).map(
            (unit, index) => {
              const placeholders: Record<typeof unit, string> = {
                years: 'Y',
                months: 'Mo',
                days: 'D',
                hours: 'H',
                minutes: 'Mi',
              };

              return (
                <TextField
                  key={index}
                  type="number"
                  variant="outlined"
                  size="small"
                  placeholder={placeholders[unit]}
                  value={interval[unit] || ''}
                  sx={compactInputSx}
                  onChange={e => handleIntervalChange(unit, e.target.value)}
                  slotProps={{
                    htmlInput: { autoComplete: 'off' },
                  }}
                />
              );
            },
          )}
        </>
      )}
    </Stack>
  );
};

const getDateDefaultValue = (node: any, removeTime = false) => {
  try {
    if (isSQLIntervalString(node)) {
      return node;
    } else {
      let date = new Date(node);

      if (removeTime) {
        date = new Date(
          date.getUTCFullYear(),
          date.getUTCMonth(),
          date.getUTCDate(),
          12,
          0,
          0,
          0,
        );
        date.setUTCHours(12, 0, 0, 0);
      }

      return date.toISOString();
    }
  } catch (e) {
    console.error('Error parsing date', e);
    return new Date().toISOString();
  }
};

export const Scalar = ({ name, node, path, where, setWhere }: ScalarProps) => {
  const { getTypeFromPath, getValueFromPath } = useSchema();
  const type = getTypeFromPath(path);

  if (!type) {
    console.info('No type found for', path);
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        No type found for {path.join('.')}
      </Typography>
    );
  }

  let inputType;
  let defaultValue;

  switch (type?.name) {
    case 'String':
      inputType = 'text';
      defaultValue = node.toString();
      break;
    case 'Int':
    case 'Float':
    case 'float8':
      inputType = 'number';
      defaultValue = parseFloat(node);
      break;
    case 'date':
      inputType = 'date';
      defaultValue = getDateDefaultValue(node, true);
      break;
    case 'timestamptz': {
      inputType = 'datetime-local';
      defaultValue = getDateDefaultValue(node);
      break;
    }
    case 'jsonb':
      inputType = 'text';
      defaultValue = JSON.stringify(node);
      break;
    case 'Boolean':
      inputType = 'checkbox';
      defaultValue = node;
      break;
    default:
      defaultValue = undefined;
      inputType = 'text';
  }

  if (Array.isArray(node)) {
    return (
      <Autocomplete
        value={node}
        multiple
        freeSolo
        size="small"
        options={[]}
        popupIcon={autocompletePopupIcon}
        clearIcon={autocompleteClearIcon}
        onChange={(e, value) => {
          let newWhere = deleteWhereClause(where, path);
          if (value) {
            newWhere = addWhereClause(newWhere, path, value);
            setWhere(newWhere);
          }
        }}
        renderInput={params => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={`+ Add filter (${name})`}
            size="small"
          />
        )}
        sx={{ minWidth: 280, ...autocompleteSx }}
      />
    );
  }

  if (['date', 'datetime-local'].includes(inputType)) {
    return (
      <TimestamptzField
        defaultValue={defaultValue}
        where={where}
        path={path}
        dateOnly={inputType === 'date'}
        onChange={(value: any) => {
          const parentPath = path.slice(0, -1);
          const parentValue = getValueFromPath(parentPath, where);
          const currentKey = path[path.length - 1];

          parentValue[currentKey] = value;
          let newWhere = deleteWhereClause(where, parentPath);

          if (value) {
            newWhere = addWhereClause(newWhere, parentPath, parentValue);
            setWhere(newWhere);
          }
        }}
      />
    );
  }

  if (inputType === 'text') {
    return (
      <TextField
        variant="outlined"
        size="small"
        defaultValue={defaultValue}
        onChange={e => {
          const newWhere = addWhereClause(where, path, e.target.value);
          setWhere(newWhere);
        }}
        sx={inputSx}
      />
    );
  }

  if (inputType === 'number') {
    return (
      <TextField
        type="number"
        variant="outlined"
        size="small"
        defaultValue={defaultValue}
        onChange={e => {
          const newWhere = addWhereClause(
            where,
            path,
            parseFloat(e.target.value),
          );
          setWhere(newWhere);
        }}
        sx={inputSx}
      />
    );
  }

  if (inputType === 'checkbox') {
    return (
      <input
        type="checkbox"
        defaultChecked={defaultValue}
        onChange={() => {
          const newWhere = addWhereClause(where, path, !node);
          setWhere(newWhere);
        }}
      />
    );
  }

  return (
    <Stack
      direction="row"
      sx={{
        gap: 0.5,
        alignItems: 'center',
      }}
    >
      <Typography
        variant="body2"
        component="label"
        sx={{
          fontStyle: 'italic',
          color: 'text.secondary',
        }}
      >
        {name}
      </Typography>
      <TextField
        type={inputType}
        onChange={e => {
          const newWhere = addWhereClause(where, path, e.target.value);
          setWhere(newWhere);
        }}
        defaultValue={defaultValue}
        variant="outlined"
        size="small"
        sx={inputSx}
      />
      <DeleteButton
        onClick={() => {
          const newWhere = deleteWhereClause(where, path);
          setWhere(newWhere);
        }}
      />
    </Stack>
  );
};
